import { useLocation, useRouteLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useFeature } from '~/modules/root/hook/use-feature';
import { useEditPermissions } from '~/modules/user';
import { getSubRoutes } from '@modules/professional/component/sub-route-navigation/utils';
import type { ProfessionalInitialResult } from '@modules/professional/hook/use-professional-initial-route-data';
import type { loader as layoutLoader } from '@modules/professional/page/loaders/professional-page-loader.server';
import {
    getActiveTabLink,
    isOnTabPath,
} from '~/ui-rocco/pages/professional-detail/utils/subnav-active';
import { buildProfessionalNavItems } from '~/ui-rocco/pages/professional-detail/utils/professional-nav-items';
import { ProfessionalContactTabButton } from './ProfessionalContactTabButton';

/** Match the Figma's tab labels (legacy uses Profile). */
const LABEL_OVERRIDES: Partial<Record<string, string>> = {
    Profile: 'Overview',
};

/** Tabs whose body is dark — the pill bar flips to the inverted palette and
 *  sits flush above the dark section (Projects case-studies + Contact). */
const DARK_TAB_SUFFIXES = ['/case-studies', '/contact'];

/**
 * Desktop pill tab bar below the hero (Figma / BC-4505) — the professional's
 * available tabs as rounded pills, the active tab a filled pill. Flips to the
 * dark palette on the dark Projects tab. Lives in the persistent layout so it
 * renders once and never re-flashes on tab nav. Mobile uses the underline
 * {@link import('./ProfessionalSubNav').ProfessionalSubNav} instead.
 */
export const ProfessionalDesktopSubNav = () => {
    const layoutData = useRouteLoaderData<typeof layoutLoader>(
        'routes/_app.professional.$urlSegment',
    );
    const { pathname } = useLocation();
    const imageLabelSearchEnabled = useFeature('image_label_search_profile');
    const initial = layoutData?.initial;
    // Editors with Products allowed but no products yet still need the Products
    // tab to add their first product — mirror the legacy nav's edit permission.
    const { canEditProducts } = useEditPermissions(initial?.ID ?? 0);
    if (!initial) return null;

    const currentPath = pathname.replace(/\/+$/, '');
    const base = `/professional/${initial.URLSegment}`;
    // Segment-aware so the dark bar survives filter path segments written under
    // a dark tab (e.g. /case-studies/category_kitchen).
    const isDark = DARK_TAB_SUFFIXES.some((suffix) =>
        isOnTabPath(`${base}${suffix}`, currentPath),
    );
    const professional = initial as unknown as ProfessionalInitialResult;
    // Match prod's tab set: pass linked-profile locations (Contact) and the
    // image-label flag (Spaces) through, exactly as the legacy nav does.
    const items = getSubRoutes(
        professional,
        canEditProducts,
        layoutData?.linkedProfiles?.linkedProfilesList,
        imageLabelSearchEnabled,
    );
    const navItems = buildProfessionalNavItems(items, {
        hasDownloads: professional.HasDownloads,
    });

    const tabLinks = navItems.map((item) =>
        `${base}${item.link}`.replace(/\/+$/, ''),
    );
    const activeTabLink = getActiveTabLink(tabLinks, currentPath);

    return (
        <nav
            // Background on the full-width nav (the dark theme band must run
            // edge to edge); the pills cap at the centred 1920 container.
            className={cn(isDark ? 'bg-[#181818]' : 'bg-white')}
        >
            <div className="mx-auto flex w-full max-w-[120rem] flex-wrap items-center gap-[0.564rem] px-[3rem] pt-[4.0625rem]">
                {navItems.map((item) => {
                    const to = `${base}${item.link}`.replace(/\/+$/, '');
                    const isActive = to === activeTabLink;
                    const tabClassName = cn(
                        'inline-flex shrink-0 items-center justify-center rounded-full px-[1.176rem] py-[0.6rem] font-sans text-[1.008rem] whitespace-nowrap transition-colors',
                        isActive && isDark && 'bg-white text-[#181818]',
                        isActive && !isDark && 'bg-[#1f1f21] text-white',
                        !isActive &&
                            isDark &&
                            'border-[1.347px] border-white/40 text-white hover:bg-white hover:text-[#181818]',
                        !isActive &&
                            !isDark &&
                            'border-[1.347px] border-[#181818] text-[#181818] hover:bg-[#181818] hover:text-white',
                    );
                    if (item.opensEnquiry) {
                        return (
                            <ProfessionalContactTabButton
                                key={item.link}
                                className={tabClassName}
                            />
                        );
                    }
                    return (
                        <RoccoLink
                            key={item.link}
                            to={to}
                            // "intent" (hover/focus), not "render": every tab
                            // loader re-runs the overview query, so rendering
                            // nine prefetch links fired nine heavy backend
                            // round-trips per page view and starved the
                            // navigation the user actually made.
                            prefetch="intent"
                            // No preventScrollReset: switching tab should
                            // land at the top of the new tab. Keeping the
                            // offset dropped the reader deep into unrelated
                            // content with the nav scrolled off above them.
                            className={tabClassName}
                        >
                            {LABEL_OVERRIDES[item.label] ?? item.label}
                        </RoccoLink>
                    );
                })}
            </div>
        </nav>
    );
};
